import { useMemo, useRef } from 'react';
import { showEndsAt, type ShowLike } from './battleModel';
import { useServerClockAt } from './useServerClock';
import { DROP_FLIGHT_MS, roundRevealMs, roundStarts } from './showTiming';

export const stableOrder = <T extends { id: string }>(previousIds: string[], items: T[]): T[] => {
  const byId = new Map(items.map((item) => [item.id, item]));
  const known = new Set(previousIds);
  const result = previousIds.map((id) => byId.get(id)).filter((item): item is T => item !== undefined);
  items.forEach((item, index) => {
    if (known.has(item.id)) return;
    const anchor = items.slice(index + 1).find((later) => known.has(later.id));
    const at = anchor ? result.findIndex((r) => r.id === anchor.id) : -1;
    result.splice(at === -1 ? result.length : at, 0, item);
    known.add(item.id);
  });
  return result;
};

export const useStableOrder = <T extends { id: string }>(items: T[] | undefined): T[] | undefined => {
  const previousIds = useRef<string[]>([]);
  return useMemo(() => {
    if (!items) return undefined;
    const ordered = stableOrder(previousIds.current, items);
    previousIds.current = ordered.map((item) => item.id);
    return ordered;
  }, [items]);
};

export const showEnds = (items: ShowLike[]): number[] =>
  items.map(showEndsAt).filter((ends): ends is number => ends !== null);

export const showBoundaries = (items: ShowLike[]): number[] =>
  items.flatMap((item) => {
    if (item.status !== 'COMPLETED' || !item.completedAt) return [];
    const starts = roundStarts(Date.parse(item.completedAt), item.participants.length, item.rounds);
    const reveal = roundRevealMs(item.participants.length);
    return starts.flatMap((start, i) => (i < item.rounds.length ? [start, start + reveal, start + reveal + DROP_FLIGHT_MS] : [start]));
  });

export const useShowClock = (items: ShowLike[] | undefined): number => {
  const boundaries = useMemo(() => [...showBoundaries(items ?? []), ...showEnds(items ?? [])], [items]);
  return useServerClockAt(boundaries).now;
};
